/**
 * Storage Service - Abstraction layer for Firebase Storage operations
 * §7 CLAUDE.md: Les composants n'appellent jamais Firebase directement
 */

import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  UploadTask,
  UploadTaskSnapshot
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { storage, functions } from './firebase/config';

export type { UploadTask, UploadTaskSnapshot };

// ============================================================================
// UPLOAD OPERATIONS
// ============================================================================

export interface UploadResult {
  url: string;
  path: string;
}

export interface UploadProgress {
  progress: number;
  bytesTransferred: number;
  totalBytes: number;
}

// Map quelques extensions courantes quand le navigateur ne renseigne pas file.type
const EXT_TO_CONTENT_TYPE: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
  '.bmp': 'image/bmp',
  '.avif': 'image/avif'
};

/**
 * Détermine le contentType à envoyer à Firebase Storage.
 * Essentiel car les règles de sécurité vérifient request.resource.contentType.
 */
const getContentType = (file: File | Blob, path: string): string => {
  if (file.type) {
    return file.type;
  }

  const lowerPath = path.toLowerCase();
  for (const [ext, type] of Object.entries(EXT_TO_CONTENT_TYPE)) {
    if (lowerPath.endsWith(ext)) {
      return type;
    }
  }

  return 'application/octet-stream';
};

/**
 * Upload a file and get the download URL.
 * cacheControl immutable : les objets sont adressés par nom unique (timestamp)
 * et jamais mutés — cache navigateur maximal, rechargements instantanés.
 */
export const uploadFile = async (
  path: string,
  file: File | Blob
): Promise<UploadResult> => {
  const storageRef = ref(storage, path);
  const metadata = {
    contentType: getContentType(file, path),
    cacheControl: 'public, max-age=31536000, immutable',
  };
  await uploadBytes(storageRef, file, metadata);
  const url = await getDownloadURL(storageRef);
  return { url, path };
};

/**
 * Upload a file with progress tracking
 */
export const uploadFileWithProgress = (
  path: string,
  file: File | Blob,
  onProgress?: (progress: UploadProgress) => void,
  onError?: (error: Error) => void,
  onComplete?: (result: UploadResult) => void
): UploadTask => {
  const storageRef = ref(storage, path);
  const metadata = {
    contentType: getContentType(file, path),
    cacheControl: 'public, max-age=31536000, immutable',
  };
  const uploadTask = uploadBytesResumable(storageRef, file, metadata);

  uploadTask.on(
    'state_changed',
    (snapshot) => {
      if (onProgress) {
        onProgress({
          progress: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
          bytesTransferred: snapshot.bytesTransferred,
          totalBytes: snapshot.totalBytes
        });
      }
    },
    (error) => {
      if (onError) onError(error);
    },
    async () => {
      if (onComplete) {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        onComplete({ url, path });
      }
    }
  );

  return uploadTask;
};

/**
 * PUT d'un fichier sur une URL présignée R2 (US R2-06) via XMLHttpRequest —
 * même shape UploadProgress que uploadFileWithProgress pour les barres de
 * progression. ⚠️ le header Content-Type DOIT être le contentType signé dans
 * l'URL, sinon R2 rejette la signature. Résout au 2xx, rejette sinon.
 */
export const uploadFileToPresignedUrl = (
  url: string,
  file: File,
  contentType: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (event) => {
      if (!onProgress) return;
      const totalBytes = event.lengthComputable && event.total > 0 ? event.total : file.size;
      onProgress({
        progress: totalBytes > 0 ? (event.loaded / totalBytes) * 100 : 0,
        bytesTransferred: event.loaded,
        totalBytes,
      });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Presigned upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Presigned upload failed (network error)'));
    xhr.send(file);
  });

// ============================================================================
// DELETE OPERATIONS
// ============================================================================

/**
 * Delete a file by its path
 */
export const deleteFile = async (path: string): Promise<void> => {
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
};

/**
 * Delete a file by its URL (extracts path from URL)
 */
export const deleteFileByUrl = async (url: string): Promise<void> => {
  try {
    // Extract path from Firebase Storage URL
    const match = url.match(/\/o\/(.+?)\?/);
    if (match) {
      const path = decodeURIComponent(match[1]);
      await deleteFile(path);
    }
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// ============================================================================
// URL OPERATIONS
// ============================================================================

/**
 * Get download URL for a file path
 */
export const getFileUrl = async (path: string): Promise<string> => {
  const storageRef = ref(storage, path);
  return getDownloadURL(storageRef);
};

// ============================================================================
// SIGNED MEDIA URLS (US R2-05 — migration médias → Cloudflare R2)
// ============================================================================

export interface SignMediaUrlsResponse {
  urls: Record<string, string>;
}

const signMediaUrlsFn = httpsCallable<
  { paths: string[]; width?: number },
  SignMediaUrlsResponse
>(functions, 'signMediaUrls', { timeout: 30_000 });

/**
 * Appelle la callable signMediaUrls (batch — une seule requête par écran).
 * `width` = rendition à la volée du Worker média, absente = original.
 */
export const requestSignedMediaUrls = async (
  paths: string[],
  width?: number
): Promise<SignMediaUrlsResponse> => {
  const { data } = await signMediaUrlsFn(width ? { paths, width } : { paths });
  return data;
};
